import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import { showUserErrorToast } from '../../utils/userToast';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { getCurrentUserProfileObjectId } from '../../utils';
import { formatCampaignMoney } from '../../utils/campaignCurrency';
import { getActiveWalletPlan } from '../../utils/planType';
import { MIN_WITHDRAWAL_AMOUNT } from '../../constants/wallet';
import {
  cancelWalletWithdrawal,
  fetchWalletSummary,
  fetchWalletTransactions,
  getWalletErrorMessage,
  requestWalletWithdrawal,
  WalletBalance,
  WalletPayoutMethod,
  WalletTransactionsPage,
  WalletWithdrawalRequest,
} from '../../services/walletService';
import { isValidIban, normalizeIban } from '../../utils/iban';
import PreferredBillingToggle from '../../components/PreferredBillingToggle/PreferredBillingToggle';
import WalletBalances from './WalletBalances';
import WalletTransactionHistory from './WalletTransactionHistory';
import { formatWalletDate } from './walletTransactionCopy';
import '../Earnings/CuratorEarningsPage.css';
import { useLiveDataRefresh } from '../../hooks/useLiveDataRefresh';
import './CuratorWalletPage.css';

const MIN_WITHDRAW_HINT = `Payouts start at $${MIN_WITHDRAWAL_AMOUNT} available balance. Enter any amount from $${MIN_WITHDRAWAL_AMOUNT} up to what you have available.`;

const emptyTransactions: WalletTransactionsPage = {
  items: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
};

const readStoredUserId = (): string | null => {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}')?.userId || null;
  } catch {
    return null;
  }
};

const CuratorWalletPage: React.FC = () => {
  const [auth0UserId] = useState<string | null>(() => readStoredUserId());
  const [curatorId, setCuratorId] = useState<string | null>(null);
  const [balances, setBalances] = useState<WalletBalance[]>([]);
  const [transactions, setTransactions] = useState<WalletTransactionsPage>(emptyTransactions);
  const [walletPlan] = useState(() => getActiveWalletPlan());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState('');
  const [payoutMethod, setPayoutMethod] = useState<WalletPayoutMethod>('paypal');
  const [destination, setDestination] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [activeWithdrawals, setActiveWithdrawals] = useState<WalletWithdrawalRequest[]>([]);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadWallet = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const profileId = await getCurrentUserProfileObjectId();
      if (!profileId) {
        setError('We could not find your curator profile. Connect Spotify and try again.');
        return;
      }
      setCuratorId(profileId);
      const [summary, nextTransactions] = await Promise.all([
        fetchWalletSummary(profileId),
        fetchWalletTransactions(profileId, 1),
      ]);
      setBalances(summary.balances);
      setActiveWithdrawals(summary.activeWithdrawals);
      setTransactions(nextTransactions);
    } catch (loadError) {
      setError(getWalletErrorMessage(loadError, 'Failed to load your wallet.'));
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useLiveDataRefresh(() => loadWallet({ silent: true }), { enabled: Boolean(curatorId) });

  const withdrawableBalances = balances.filter(balance => balance.available > 0);
  const selectedWithdrawCurrency = withdrawCurrency || withdrawableBalances[0]?.currencyCode || '';
  const hasAnyBalance = balances.some(
    balance =>
      balance.available > 0 ||
      (balance.eligibleEarnings || 0) > 0 ||
      balance.pendingEarnings > 0 ||
      balance.reserved > 0
  );
  const selectedAvailable =
    withdrawableBalances.find(balance => balance.currencyCode === selectedWithdrawCurrency)
      ?.available ?? 0;

  const goToTransactionsPage = async (page: number) => {
    setLoadingPage(true);
    try {
      setTransactions(await fetchWalletTransactions(curatorId, page));
    } catch (pageError) {
      showUserErrorToast(getWalletErrorMessage(pageError, 'Failed to load transactions.'));
    } finally {
      setLoadingPage(false);
    }
  };

  const submitWithdrawal = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = Number(withdrawAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter an amount greater than 0.');
      return;
    }
    if (amount < MIN_WITHDRAWAL_AMOUNT) {
      toast.error(
        `You need at least $${MIN_WITHDRAWAL_AMOUNT} available to request a payout.`
      );
      return;
    }
    if (amount > selectedAvailable) {
      toast.error(
        `Amount exceeds available balance (${formatCampaignMoney(
          selectedAvailable,
          selectedWithdrawCurrency
        )}).`
      );
      return;
    }
    if (!contactEmail.trim()) {
      toast.error('Enter an email where admin can contact you about this payout.');
      return;
    }
    const payoutDestination =
      payoutMethod === 'paypal' ? destination.trim() : normalizeIban(destination);
    if (!payoutDestination) {
      toast.error(
        payoutMethod === 'paypal' ? 'Enter your PayPal email.' : 'Enter a valid IBAN.'
      );
      return;
    }
    if (payoutMethod === 'other' && !isValidIban(payoutDestination)) {
      toast.error('Enter a valid IBAN for bank transfer.');
      return;
    }
    setWithdrawing(true);
    try {
      await requestWalletWithdrawal(curatorId, {
        amount,
        currencyCode: selectedWithdrawCurrency,
        payoutMethod,
        destination: payoutDestination,
        contactEmail: contactEmail.trim(),
        notes,
      });
      toast.success('Withdrawal requested. It is reserved until an admin pays it out.');
      setWithdrawAmount('');
      setDestination('');
      setContactEmail('');
      setNotes('');
      const summary = await fetchWalletSummary(curatorId);
      setBalances(summary.balances);
      setActiveWithdrawals(summary.activeWithdrawals);
      setTransactions(await fetchWalletTransactions(curatorId, 1));
    } catch (withdrawError) {
      showUserErrorToast(getWalletErrorMessage(withdrawError, 'Withdrawal request failed.'));
    } finally {
      setWithdrawing(false);
    }
  };

  const confirmCancelWithdrawal = (request: WalletWithdrawalRequest) => {
    if (!curatorId) return;
    Modal.confirm({
      title: 'Cancel this payout request?',
      content: `${formatCampaignMoney(request.amount, request.currencyCode)} will return to your available balance.`,
      okText: 'Cancel request',
      onOk: async () => {
        setCancellingId(request._id);
        try {
          const summary = await cancelWalletWithdrawal(curatorId, request._id);
          setBalances(summary.balances);
          setActiveWithdrawals(summary.activeWithdrawals);
          toast.success('Payout request cancelled. The balance is available again.');
        } catch (cancelError) {
          showUserErrorToast(
            getWalletErrorMessage(cancelError, 'Could not cancel this payout request.')
          );
        } finally {
          setCancellingId(null);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="earnings-dashboard">
        <LoadingSpinner text="Loading wallet..." />
      </div>
    );
  }

  return (
    <div className="earnings-dashboard">
      <div className="earnings-dashboard__header">
        <div>
          <h2>Wallet</h2>
          <p>
            Earnings start as eligible when you add a song to a campaign playlist, move to pending
            when the campaign is completed, then become available to withdraw.
          </p>
          {walletPlan ? (
            <p className="wallet-plan-note">
              Wallet {walletPlan.plan === 'pro' ? 'Pro' : 'Unlimited'} active until{' '}
              {formatWalletDate(walletPlan.periodEnd)}.
            </p>
          ) : null}
        </div>
      </div>

      <PreferredBillingToggle userId={auth0UserId} />

      {error ? (
        <div className="earnings-dashboard__error">
          <p>{error}</p>
          <button type="button" onClick={() => void loadWallet()}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <WalletBalances balances={balances} hasAnyBalance={hasAnyBalance} />

          <section className="analytics-card wallet-payout-card">
            <h3>Request payout</h3>
            <p className="analytics-empty">{MIN_WITHDRAW_HINT}</p>
            {withdrawableBalances.length === 0 ? (
              <p className="analytics-empty">
                Nothing available to withdraw yet
                {balances.some(balance => (balance.eligibleEarnings || 0) > 0)
                  ? ' — eligible earnings become pending when the campaign is completed.'
                  : balances.some(balance => balance.pendingEarnings > 0)
                    ? ' — wait for pending earnings to clear the cooling period.'
                    : '.'}
              </p>
            ) : (
              <form className="wallet-withdraw" onSubmit={submitWithdrawal}>
                <div className="wallet-withdraw__row">
                  <label>
                    Currency
                    <select
                      value={selectedWithdrawCurrency}
                      onChange={event => setWithdrawCurrency(event.target.value)}
                    >
                      {withdrawableBalances.map(balance => (
                        <option key={balance.currencyCode} value={balance.currencyCode}>
                          {balance.currencyCode} (
                          {formatCampaignMoney(balance.available, balance.currencyCode)} available)
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Amount (min ${MIN_WITHDRAWAL_AMOUNT})
                    <input
                      type="number"
                      min={MIN_WITHDRAWAL_AMOUNT}
                      max={selectedAvailable}
                      step="0.01"
                      value={withdrawAmount}
                      onChange={event => setWithdrawAmount(event.target.value)}
                      placeholder={`e.g. ${MIN_WITHDRAWAL_AMOUNT} (up to ${selectedAvailable.toFixed(2)})`}
                    />
                  </label>
                </div>
                <div className="wallet-withdraw__row">
                  <label>
                    Pay out via
                    <select
                      value={payoutMethod}
                      onChange={event => {
                        setPayoutMethod(event.target.value as WalletPayoutMethod);
                        setDestination('');
                      }}
                    >
                      <option value="paypal">PayPal</option>
                      <option value="other">Bank transfer (IBAN)</option>
                    </select>
                  </label>
                  <label>
                    {payoutMethod === 'paypal' ? 'PayPal email' : 'IBAN'}
                    <input
                      type={payoutMethod === 'paypal' ? 'email' : 'text'}
                      value={destination}
                      onChange={event => setDestination(event.target.value)}
                      placeholder={
                        payoutMethod === 'paypal'
                          ? 'you@example.com'
                          : 'DE89 3704 0044 0532 0130 00'
                      }
                      autoComplete={payoutMethod === 'paypal' ? 'email' : 'off'}
                      required
                    />
                  </label>
                </div>
                <label>
                  Contact email for admin
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={event => setContactEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </label>
                <label>
                  Notes for admin (optional)
                  <textarea
                    value={notes}
                    onChange={event => setNotes(event.target.value)}
                    rows={3}
                    placeholder="Anything the admin should know before paying you."
                  />
                </label>
                <button type="submit" className="earnings-dashboard__link-button" disabled={withdrawing}>
                  {withdrawing ? 'Requesting...' : 'Request payout'}
                </button>
                <small>
                  The amount is reserved right away and leaves your available balance until the
                  payout is completed or you cancel the request.
                </small>
              </form>
            )}
          </section>

          {activeWithdrawals.length > 0 ? (
            <section className="earnings-dashboard__history">
              <div className="history-header">
                <h3>Open payout requests</h3>
              </div>
              <div className="history-table-wrap">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Requested</th>
                      <th>Amount</th>
                      <th>Pay to</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeWithdrawals.map(request => (
                      <tr key={request._id}>
                        <td>{formatWalletDate(request.createdAt)}</td>
                        <td>{formatCampaignMoney(request.amount, request.currencyCode)}</td>
                        <td>
                          {request.payoutMethod === 'paypal' ? 'PayPal' : 'IBAN'}:{' '}
                          {request.destination}
                          {request.contactEmail ? ` · ${request.contactEmail}` : ''}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="earnings-dashboard__link-button"
                            disabled={cancellingId === request._id}
                            onClick={() => confirmCancelWithdrawal(request)}
                          >
                            {cancellingId === request._id ? 'Cancelling...' : 'Cancel request'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <WalletTransactionHistory
            transactions={transactions}
            loadingPage={loadingPage}
            onPageChange={goToTransactionsPage}
          />
        </>
      )}
    </div>
  );
};

export default CuratorWalletPage;
